import { createTheme } from '@mui/material/styles'
import React from 'react'

declare module '@mui/material/styles' {
    interface TypographyVariants {
        heroHeading: React.CSSProperties
        sectionTitle: React.CSSProperties
        sectionSubtitle: React.CSSProperties
        productPageTitle: React.CSSProperties
        textSize1: React.CSSProperties
        textSize2: React.CSSProperties
        textSize2Regular: React.CSSProperties
        textSize3: React.CSSProperties
        textSize3Regular: React.CSSProperties
        legalDisclaimer: React.CSSProperties
        textButton: React.CSSProperties
    }

    interface TypographyVariantsOptions {
        heroHeading?: React.CSSProperties
        sectionTitle?: React.CSSProperties
        sectionSubtitle?: React.CSSProperties
        productPageTitle?: React.CSSProperties
        textSize1?: React.CSSProperties
        textSize2?: React.CSSProperties
        textSize2Regular?: React.CSSProperties
        textSize3?: React.CSSProperties
        textSize3Regular?: React.CSSProperties
        legalDisclaimer?: React.CSSProperties
        textButton?: React.CSSProperties
    }

    interface Theme {
        color: {
            figGreen: React.CSSProperties['color']
            figGreenDark: React.CSSProperties['color']
            figGreenLight: React.CSSProperties['color']
            figSand: React.CSSProperties['color']
            secondaryGreen: React.CSSProperties['color']
            secondaryGreenLight: React.CSSProperties['color']
            secondaryPurple: React.CSSProperties['color']
            secondaryPurpleLight: React.CSSProperties['color']
            secondaryOrange: React.CSSProperties['color']
            secondaryOrangeLight: React.CSSProperties['color']
            white?: React.CSSProperties['color']
            grey400?: React.CSSProperties['color']
            grey600?: React.CSSProperties['color']
            grey700?: React.CSSProperties['color']
            grey800?: React.CSSProperties['color']
            grey900?: React.CSSProperties['color']
            greyBorder?: React.CSSProperties['color']
            warning1?: React.CSSProperties['color']
            warning2?: React.CSSProperties['color']
        }
    }

    interface ThemeOptions {
        color?: {
            figGreen?: React.CSSProperties['color']
            figGreenDark?: React.CSSProperties['color']
            figGreenLight?: React.CSSProperties['color']
            figSand?: React.CSSProperties['color']
            secondaryGreen?: React.CSSProperties['color']
            secondaryGreenLight?: React.CSSProperties['color']
            secondaryPurple?: React.CSSProperties['color']
            secondaryPurpleLight?: React.CSSProperties['color']
            secondaryOrange?: React.CSSProperties['color']
            secondaryOrangeLight?: React.CSSProperties['color']
            white?: React.CSSProperties['color']
            grey400?: React.CSSProperties['color']
            grey600?: React.CSSProperties['color']
            grey700?: React.CSSProperties['color']
            grey800?: React.CSSProperties['color']
            grey900?: React.CSSProperties['color']
            greyBorder?: React.CSSProperties['color']
            warning1?: React.CSSProperties['color']
            warning2?: React.CSSProperties['color']
        }
    }
}

declare module '@mui/material/Typography' {
    interface TypographyPropsVariantOverrides {
        heroHeading: true
        sectionTitle: true
        sectionSubtitle: true
        productPageTitle: true
        textSize1: true
        textSize2: true
        textSize2Regular: true
        textSize3: true
        textSize3Regular: true
        legalDisclaimer: true
        textButton: true
    }
}

export const customColors = {
    figGreen: '#005151',
    figGreenLight: '#ECFBF7', // spearmint
    figGreenDark: '#083633',
    figSand: '#F7F7EB',
    secondaryGreen: '#00B8A2',
    secondaryGreenLight: '#7AF0D4',
    secondaryPurple: '#A32170',
    secondaryPurpleLight: '#F7ADE3',
    secondaryOrange: '#E3522B',
    secondaryOrangeLight: '#F2A887',
    white: '#FFFFFF',
    grey400: '#D1D5DB',
    grey600: '#6B7280',
    grey700: '#4B5563',
    grey800: '#1F2937',
    grey900: '#111827',
    greyBorder: '#E5E7EB',
    warning1: '#754900',
    warning2: '#FEF3C7'
}

export const theme = createTheme({
    color: {
        ...customColors
    },
    palette: {
        primary: {
            main: customColors.figGreen
        },
        secondary: {
            main: customColors.figGreenDark
        },
        background: {
            default: customColors.figSand
        }
    },
    typography: {
        fontFamily: '"basic-sans", sans-serif',
        heroHeading: {
            fontFamily: '"basic-sans", sans-serif',
            fontSize: '64px',
            fontWeight: 900,
            lineHeight: '100%',
            color: customColors.figGreenDark
        },
        sectionTitle: {
            fontFamily: '"basic-sans", sans-serif',
            fontSize: '48px',
            fontWeight: 700,
            lineHeight: '100%',
            color: customColors.figGreenDark
        },
        sectionSubtitle: {
            fontFamily: '"basic-sans", sans-serif',
            fontSize: '40px',
            fontWeight: 600,
            lineHeight: '100%',
            color: customColors.figGreenDark
        },
        productPageTitle: {
            fontFamily: '"basic-sans", sans-serif',
            fontSize: '28px',
            fontWeight: 700,
            lineHeight: '100%',
            color: customColors.grey800
        },
        textSize1: {
            fontFamily: '"basic-sans", sans-serif',
            fontSize: '24px',
            fontWeight: 600,
            lineHeight: '125%',
            color: customColors.figGreenDark
        },
        textSize2: {
            fontFamily: '"basic-sans", sans-serif',
            fontSize: '20px',
            fontWeight: 600,
            lineHeight: '100%',
            color: customColors.figGreenDark
        },
        textSize2Regular: {
            fontFamily: '"basic-sans", sans-serif',
            fontSize: '20px',
            fontWeight: 400,
            lineHeight: '100%',
            color: customColors.figGreenDark
        },
        textSize3: {
            fontFamily: '"basic-sans", sans-serif',
            fontSize: '16px',
            fontWeight: 600,
            lineHeight: '125%',
            color: customColors.figGreenDark
        },
        textSize3Regular: {
            fontFamily: '"basic-sans", sans-serif',
            fontSize: '16px',
            fontWeight: 400,
            lineHeight: '125%',
            color: customColors.figGreenDark
        },
        legalDisclaimer: {
            fontFamily: '"basic-sans", sans-serif',
            fontSize: '14px',
            fontWeight: 400,
            lineHeight: '125%',
            color: customColors.figGreenDark
        },
        textButton: {
            fontFamily: '"basic-sans", sans-serif',
            fontSize: '16px',
            fontWeight: 700,
            lineHeight: '125%',
            color: customColors.white
        }
    },
    components: {
        MuiTypography: {
            defaultProps: {
                variantMapping: {
                    heroHeading: 'h1',
                    sectionTitle: 'h2',
                    sectionSubtitle: 'h2',
                    productPageTitle: 'h1',
                    textSize1: 'p',
                    textSize2: 'p',
                    textSize2Regular: 'p',
                    textSize3: 'p',
                    textSize3Regular: 'p',
                    legalDisclaimer: 'p',
                    textButton: 'p'
                }
            }
        },
        MuiButton: {
            styleOverrides: {
                root: {
                    textTransform: 'inherit',
                    minWidth: 'auto',
                    fontWeight: 600,
                    fontSize: '16px',
                    lineHeight: '20px',
                    fontStyle: 'normal',
                    width: '100%',
                    borderRadius: '100px',
                    padding: '12px',
                    height: '46px',
                    backgroundColor: 'none',
                    boxShadow: 'none',
                    '&:hover': {
                        boxShadow: 'none'
                    },
                    '&:disabled': {
                        cursor: 'not-allowed'
                    },
                    '&.MuiButton-contained:disabled': {
                        backgroundColor: customColors.grey400,
                        color: customColors.grey700
                    }
                }
            }
        }
    }
})
